import { useEffect, useMemo, useState } from 'react';

import {
  Box,
  Button,
  ButtonGroup,
  Center,
  Flex,
  HStack,
  Input,
  InputGroup,
  InputRightElement,
  Text,
  VStack,
} from '@chakra-ui/react';

import { SavingsCategory, SearchNormalOutline, useToast } from '@aslanpay/portal-common-lib';

import SavingsCategoriesManager from 'app/components/SavingsCategories/SavingsCategoriesManager';
import UnusedPresetImages from 'app/components/SavingsCategories/UnusedPresetImages';
import { PresetImageOption, SavingsCategoryScope } from 'app/components/SavingsCategories/types';

import useDebounce from 'app/hooks/useDebounce';
import useFetchSavingsCategories from 'app/hooks/useFetchSavingsCategories';
import useFetchSavingsPresetImages from 'app/hooks/useFetchSavingsPresetImages';
import useSavingsCategoryMutations from 'app/hooks/useSavingsCategoryMutations';
import useSearchCompanies from 'app/hooks/useSearchCompanies';

interface SelectedCompany {
  id: string;
  name: string;
}

/**
 * Container for savings-category management: switches between the global set and a per-company set
 * (via a company search), wires the list / mutation hooks and toasts, and derives whether the
 * current scope is editable or a read-only global fallback.
 */
function SavingsCategoriesSection() {
  const toast = useToast();
  const [scope, setScope] = useState<SavingsCategoryScope>('global');
  const [selectedCompany, setSelectedCompany] = useState<SelectedCompany | undefined>(undefined);
  const [query, setQuery] = useState('');

  const companyId = scope === 'company' ? selectedCompany?.id : undefined;
  const isEnabled = scope === 'global' || !!selectedCompany;

  const { data, isInitialLoading } = useFetchSavingsCategories(companyId, isEnabled);
  const { data: presetData } = useFetchSavingsPresetImages();
  const mutations = useSavingsCategoryMutations();
  const { data: companyResults = [] } = useSearchCompanies(
    query,
    scope === 'company' && !selectedCompany
  );
  const debouncedSetQuery = useDebounce((value: string) => setQuery(value));

  // Memoised so the local-order sync effect only runs when the server list actually changes.
  const categories = useMemo(() => data?.list ?? [], [data]);

  // Local copy of the category order so drag-reordering updates the UI instantly; it is re-synced
  // from the server whenever the fetched list changes, and reverted if a reorder fails to persist.
  const [orderedCategories, setOrderedCategories] = useState<SavingsCategory[]>([]);

  useEffect(() => {
    setOrderedCategories(categories);
  }, [categories]);

  const presetImages: PresetImageOption[] = useMemo(
    () =>
      (presetData?.list ?? [])
        .filter((preset) => preset.status === 'SUCCEEDED')
        .map((preset) => ({ id: preset.id, name: preset.name })),
    [presetData]
  );

  // Preset images not referenced by any currently-loaded category (global scope only).
  const unusedImages = useMemo(() => {
    const usedImageIds = new Set(
      categories.flatMap((category) => category.images.map((image) => image.imageId))
    );
    return presetImages.filter((preset) => !usedImageIds.has(preset.id));
  }, [categories, presetImages]);

  const hasOwnSet =
    scope === 'company' &&
    !!selectedCompany &&
    categories.some((category) => category.companyId === selectedCompany.id);
  const isFallback = scope === 'company' && !!selectedCompany && !hasOwnSet;
  const canEdit = scope === 'global' || hasOwnSet;

  const onError = () =>
    toast({
      position: 'top-right',
      status: 'error',
      title: 'Something went wrong',
      description: 'The change could not be saved. Please try again.',
    });

  const handleCreate = (name: string) =>
    mutations.createCategory.mutate({ body: { name }, companyId }, { onError });

  const handleRename = (id: string, name: string) =>
    mutations.updateCategory.mutate({ id, body: { name } }, { onError });

  const handleDelete = (id: string) => mutations.deleteCategory.mutate(id, { onError });

  const handleTogglePublish = (category: SavingsCategory) =>
    mutations.updateCategory.mutate(
      { id: category.id, body: { published: !category.published } },
      { onError }
    );

  const handleSetImages = (id: string, imageIds: string[]) =>
    mutations.setCategoryImages.mutate({ id, body: { imageIds } }, { onError });

  const handleReorder = (orderedIds: string[]) => {
    const reordered = orderedIds
      .map((id) => orderedCategories.find((category) => category.id === id))
      .filter((category): category is SavingsCategory => !!category);

    // Update the visible order immediately, then persist each moved category's position.
    setOrderedCategories(reordered);

    const revertOnError = () => {
      setOrderedCategories(categories);
      onError();
    };

    reordered.forEach((category, index) => {
      if (category.displayOrder !== index) {
        mutations.updateCategory.mutate(
          { id: category.id, body: { displayOrder: index } },
          { onError: revertOnError }
        );
      }
    });
  };

  const handleClone = () => {
    if (selectedCompany) {
      mutations.cloneCategories.mutate(selectedCompany.id, { onError });
    }
  };

  const handleReset = () => {
    if (selectedCompany) {
      mutations.resetCategories.mutate(selectedCompany.id, { onError });
    }
  };

  const handleAssignImage = (categoryId: string, imageId: string) => {
    const category = categories.find((candidate) => candidate.id === categoryId);
    if (!category) {
      return;
    }
    const orderedIds = [...category.images]
      .sort((first, second) => first.displayOrder - second.displayOrder)
      .map((image) => image.imageId);
    const next = orderedIds.includes(imageId) ? orderedIds : [...orderedIds, imageId];
    mutations.setCategoryImages.mutate(
      { id: categoryId, body: { imageIds: next } },
      {
        onSuccess: () =>
          toast({
            position: 'top-right',
            status: 'success',
            title: 'Assigned',
            description: 'The image was added to the category.',
          }),
        onError,
      }
    );
  };

  const switchScope = (next: SavingsCategoryScope) => {
    setScope(next);
    if (next === 'global') {
      setSelectedCompany(undefined);
      setQuery('');
    }
  };

  return (
    <Flex direction="column" gap="20px" w="full">
      <Text textStyle="smallRegular" color="brand.gray">
        Choose whether to manage the shared global categories or a specific company&apos;s own set.
      </Text>

      <Flex align="center" gap="16px" wrap="wrap">
        <ButtonGroup isAttached size="sm">
          <Button
            variant={scope === 'global' ? 'solid' : 'outline'}
            onClick={() => switchScope('global')}
          >
            Global
          </Button>
          <Button
            variant={scope === 'company' ? 'solid' : 'outline'}
            onClick={() => switchScope('company')}
          >
            Company
          </Button>
        </ButtonGroup>

        {scope === 'company' &&
          (selectedCompany ? (
            <HStack spacing="8px">
              <Text textStyle="smallMedium" color="brand.black">
                Managing: {selectedCompany.name}
              </Text>
              <Button
                variant="link"
                size="sm"
                onClick={() => {
                  setSelectedCompany(undefined);
                  setQuery('');
                }}
              >
                Change
              </Button>
            </HStack>
          ) : (
            <Box position="relative" minW="278px">
              <InputGroup color="brand.darkGray">
                <Input
                  defaultValue=""
                  type="text"
                  placeholder="Search by company ID, name or alias"
                  onChange={(event) => debouncedSetQuery(event.target.value)}
                  data-testid="company-search-input"
                />
                <InputRightElement w="56px" h="full">
                  <SearchNormalOutline boxSize="24px" />
                </InputRightElement>
              </InputGroup>
              {query.length >= 3 && companyResults.length > 0 && (
                <VStack
                  align="stretch"
                  position="absolute"
                  zIndex={2}
                  mt="4px"
                  w="full"
                  bg="white"
                  border="1px solid"
                  borderColor="brand.lightGray1"
                  borderRadius="8px"
                  boxShadow="md"
                  spacing="0"
                  maxH="240px"
                  overflowY="auto"
                >
                  {companyResults.map((company) => (
                    <Button
                      key={company.id}
                      variant="ghost"
                      justifyContent="flex-start"
                      borderRadius="0"
                      onClick={() => {
                        setSelectedCompany({ id: company.id, name: company.name });
                        setQuery('');
                      }}
                    >
                      {company.name}
                    </Button>
                  ))}
                </VStack>
              )}
            </Box>
          ))}
      </Flex>

      {scope === 'company' && !selectedCompany ? (
        <Center py="40px">
          <Text textStyle="smallRegular" color="brand.gray">
            Select a company to manage its savings categories.
          </Text>
        </Center>
      ) : (
        <SavingsCategoriesManager
          categories={orderedCategories}
          presetImages={presetImages}
          isLoading={isInitialLoading}
          isCreating={mutations.createCategory.isLoading}
          isRenaming={mutations.updateCategory.isLoading}
          isSettingImages={mutations.setCategoryImages.isLoading}
          isCloning={mutations.cloneCategories.isLoading}
          isResetting={mutations.resetCategories.isLoading}
          canEdit={canEdit}
          isFallback={isFallback}
          onCreate={handleCreate}
          onRename={handleRename}
          onDelete={handleDelete}
          onReorder={handleReorder}
          onTogglePublish={handleTogglePublish}
          onSetImages={handleSetImages}
          onCloneGlobal={handleClone}
          onResetToGlobal={handleReset}
        />
      )}

      {scope === 'global' && unusedImages.length > 0 && (
        <UnusedPresetImages
          images={unusedImages}
          categories={orderedCategories.map((category) => ({
            id: category.id,
            name: category.name,
          }))}
          onAssign={handleAssignImage}
        />
      )}
    </Flex>
  );
}

export default SavingsCategoriesSection;
